/**
 * DevToolsPage — Narzędzia developerskie.
 *
 * Od 2026-02: PRZEBUDOWANE UX. Zamiast jednej długiej przewijanej strony —
 * cztery główne sekcje wybierane kaflami (styl 1:1 z Paczek Płatności, mobile
 * 2×2, desktop 1×4):
 *   1. Usuwanie danych        — wszystkie akcje resetujące (7 operacji + Reset matching)
 *   2. Statystyki             — wszystkie liczniki + status KSeF + status mBank + Profile AI viewer
 *   3. Playground mBank PSD2  — DIAGNOSTYKA integracji mBank (placeholder na przyszłe endpointy)
 *   4. XML / KSeF             — narzędzia serwisowe KSeF (Backfill XML + skróty do kolejki)
 *
 * BRAK zmian w logice biznesowej. Endpointy backend niezmienione. Zachowane
 * wszystkie stare `data-testid` — testy z poprzednich iteracji nadal działają.
 *
 * Liczniki pobierane z `GET /api/dev-tools/stats` (mobile-first, 1 round-trip).
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Card, CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Wrench, AlertTriangle, FileText, Wallet, Package, Link2, Sparkles,
  ChevronLeft, Loader2, Trash2, RefreshCw, CheckCircle2,
  Trash, BarChart3, Landmark, FileCode2, Database, Copy, Check,
  Users, ExternalLink, Play, PauseCircle, Activity, Lock, Unlock, Skull, HeartPulse,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import AiProfilesViewer from "./AiProfilesViewer";
import KillSwitchSection from "./KillSwitchSection";
import AiHealthSection from "./AiHealthSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Polska pluralizacja ──────────────────────────────────────────────────────
// Trzy formy: [singular, few (2-4), many (0, 5+)]. Stosowana dla komunikatów toast.
const polishPlural = (n, forms) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (n === 1) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1];
  return forms[2];
};

// ── Operacje (deklaratywnie) ─────────────────────────────────────────────────
// Każda operacja ma: id, label, icon, color, opis (co usuwa), endpoint,
// countLabel, countKey(s) — liczone z `stats` zwracanym przez /api/dev-tools/stats.
// `resultForms` — formy Polish pluralization do toasta "Usunięto X ...".

const OPERATIONS = [
  // FAKTURY
  {
    section: "faktury",
    sectionLabel: "Faktury",
    sectionIcon: FileText,
    sectionColor: "blue",
    id: "invoices",
    label: "Usuń wszystkie faktury",
    description: "Usuwa: invoices, ksef_invoices, vendor_invoice_profiles, vendor_invoice_agent_contexts, agent_vendor_memory",
    notDeleted: "Nie usuwa: wyciągów, paczek, konfiguracji KSeF",
    endpoint: "/dev-tools/invoices",
    counts: [
      { key: "invoices_count", label: "Faktury" },
      { key: "invoice_profiles_count", label: "Profile Agenta Faktur" },
    ],
    resultForms: ["fakturę", "faktury", "faktur"],
  },
  // WYCIĄGI
  {
    section: "wyciagi",
    sectionLabel: "Wyciągi bankowe",
    sectionIcon: Wallet,
    sectionColor: "emerald",
    id: "bank-transactions",
    label: "Usuń wszystkie wyciągi",
    description: "Usuwa: bank_transactions, payment_counterparty_profiles, bank_statement_imports, bank_import_pending, bank_agent_state",
    notDeleted: "Nie usuwa: faktur, paczek, konfiguracji KSeF",
    endpoint: "/dev-tools/bank-transactions",
    counts: [
      { key: "bank_transactions_count", label: "Transakcje bankowe" },
      { key: "bank_profiles_count", label: "Profile Agenta Wyciągów" },
    ],
    resultForms: ["transakcję bankową", "transakcje bankowe", "transakcji bankowych"],
  },
  // PACZKI
  {
    section: "paczki",
    sectionLabel: "Paczki płatności",
    sectionIcon: Package,
    sectionColor: "indigo",
    id: "payment-packages",
    label: "Usuń wszystkie paczki",
    description: "Usuwa: payment_packages (proposal/draft/rejected/cancelled)",
    notDeleted: "Nie usuwa: faktur, wyciągów, konfiguracji KSeF",
    endpoint: "/dev-tools/payment-packages",
    counts: [
      { key: "payment_proposals_count", label: "Propozycje" },
      { key: "payment_packages_count", label: "Paczki draft" },
      { key: "payment_analyses_count", label: "Łącznie analiz" },
    ],
    resultForms: ["paczkę płatności", "paczki płatności", "paczek płatności"],
  },
  // MATCHING
  {
    section: "matching",
    sectionLabel: "Dopasowania",
    sectionIcon: Link2,
    sectionColor: "violet",
    id: "matching",
    label: "Usuń wszystkie dopasowania",
    description: "Usuwa dane matchingu Bank ↔ Faktura (jeżeli persystowane)",
    notDeleted: "Matching obliczany w locie z IBAN — brak persystencji",
    endpoint: "/dev-tools/matching",
    counts: [
      { key: "matching_count", label: "Dopasowania" },
    ],
    disabledWhenZero: true,
    resultForms: ["dopasowanie", "dopasowania", "dopasowań"],
  },
  // PROFILE AI — 3 osobne
  {
    section: "profile",
    sectionLabel: "Profile AI",
    sectionIcon: Sparkles,
    sectionColor: "amber",
    id: "invoice-profiles",
    label: "Usuń profile Agenta Faktur",
    description: "Usuwa: vendor_invoice_profiles, vendor_invoice_agent_contexts, agent_vendor_memory",
    notDeleted: "Nie usuwa: faktur, ustawień agenta",
    endpoint: "/dev-tools/invoice-profiles",
    counts: [
      { key: "invoice_profiles_count", label: "Profile faktur" },
      { key: "invoice_agent_memory_count", label: "Pamięć agenta" },
    ],
    resultForms: ["profil Agenta Faktur", "profile Agenta Faktur", "profili Agenta Faktur"],
  },
  {
    section: "profile",
    sectionLabel: "Profile AI",
    sectionIcon: Sparkles,
    sectionColor: "amber",
    id: "bank-profiles",
    label: "Usuń profile Agenta Wyciągów",
    description: "Usuwa: payment_counterparty_profiles",
    notDeleted: "Nie usuwa: transakcji, importów, ustawień agenta",
    endpoint: "/dev-tools/bank-profiles",
    counts: [
      { key: "bank_profiles_count", label: "Profile bankowe" },
    ],
    resultForms: ["profil Agenta Wyciągów", "profile Agenta Wyciągów", "profili Agenta Wyciągów"],
  },
  {
    section: "profile",
    sectionLabel: "Profile AI",
    sectionIcon: Sparkles,
    sectionColor: "amber",
    id: "payment-profiles",
    label: "Usuń profile Agenta Płatności",
    description: "Usuwa propozycje AI (proposal/rejected) z payment_packages",
    notDeleted: "Nie usuwa: paczek draft, faktur, wyciągów",
    endpoint: "/dev-tools/payment-profiles",
    counts: [
      { key: "payment_proposals_count", label: "Propozycje AI" },
    ],
    resultForms: ["profil Agenta Płatności", "profile Agenta Płatności", "profili Agenta Płatności"],
  },
];

// ── Komponenty ───────────────────────────────────────────────────────────────

const COLOR_MAP = {
  blue: { bg: "bg-blue-50", text: "text-blue-600" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-600" },
  indigo: { bg: "bg-indigo-50", text: "text-indigo-600" },
  violet: { bg: "bg-violet-50", text: "text-violet-600" },
  amber: { bg: "bg-amber-50", text: "text-amber-600" },
};

const CountChip = ({ label, value, loading }) => (
  <div className="bg-gray-50 border border-gray-100 rounded px-2.5 py-1.5 min-w-0 flex-1">
    <div className="text-[10px] text-gray-500 truncate">{label}</div>
    <div className="font-mono text-sm font-semibold text-gray-900" data-testid={`dev-count-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      {loading ? "—" : (value ?? 0)}
    </div>
  </div>
);

const OperationCard = ({ op, stats, loading, busyId, onDelete }) => {
  const Icon = op.sectionIcon;
  const color = COLOR_MAP[op.sectionColor] || COLOR_MAP.blue;
  const totalCount = op.counts.reduce((acc, c) => acc + (stats?.[c.key] ?? 0), 0);
  const zero = totalCount === 0;
  const isBusy = busyId === op.id;
  const disabled = loading || isBusy || (op.disabledWhenZero && zero);
  return (
    <Card className="border border-gray-200" data-testid={`dev-op-card-${op.id}`}>
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className={`p-2 rounded-md shrink-0 ${color.bg}`}>
            <Icon className={`w-4 h-4 ${color.text}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900">{op.label}</div>
            <div className="text-[11px] text-gray-500 break-words mt-0.5">{op.description}</div>
            <div className="text-[11px] text-emerald-700 break-words mt-0.5">{op.notDeleted}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {op.counts.map((c) => (
            <CountChip
              key={c.key}
              label={c.label}
              value={stats?.[c.key]}
              loading={loading}
            />
          ))}
        </div>
        <Button
          onClick={() => onDelete(op)}
          disabled={disabled}
          className="w-full sm:w-auto bg-rose-600 hover:bg-rose-700 text-white disabled:bg-gray-300"
          data-testid={`dev-op-btn-${op.id}`}
        >
          {isBusy
            ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            : <Trash2 className="w-4 h-4 mr-1.5" />}
          {op.label}
        </Button>
      </CardContent>
    </Card>
  );
};

// ── Strona ───────────────────────────────────────────────────────────────────

export default function DevToolsPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  // Aktywna sekcja — 6 kafli. Persist w localStorage żeby nie wracać do domyślnej po odświeżeniu.
  const [activeSection, setActiveSection] = useState(() => {
    try {
      const saved = localStorage.getItem("pax.dev-tools.section");
      return ["delete", "stats", "mbank", "xml", "emergency", "ai-health"].includes(saved) ? saved : "delete";
    } catch { return "delete"; }
  });
  useEffect(() => {
    try { localStorage.setItem("pax.dev-tools.section", activeSection); } catch { /* noop */ }
  }, [activeSection]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/dev-tools/stats`, { withCredentials: true });
      setStats(r.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd pobierania liczników.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleDelete = async (op) => {
    setBusyId(op.id);
    try {
      const r = await axios.delete(`${API}${op.endpoint}`, { withCredentials: true });
      const deleted = r.data?.deleted_count ?? 0;
      const noun = polishPlural(deleted, op.resultForms);
      // Rozszerzona odpowiedź dla paczek — dokładamy informację
      // o odblokowanych fakturach (audit DEVTOOLS_CLEAR_ALL_PACKAGES).
      // Pozostałe endpointy zachowują dotychczasowy toast.
      const unlocked = r.data?.unlocked_invoices_count;
      if (op.id === "payment-packages" && typeof unlocked === "number" && unlocked > 0) {
        toast.success(
          `Usunięto ${deleted} ${noun} · odblokowano ${unlocked} ${polishPlural(unlocked, ["fakturę","faktury","faktur"])}.`
        );
      } else {
        toast.success(`Usunięto ${deleted} ${noun}.`);
      }
      await fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd usuwania.");
    } finally {
      setBusyId(null);
    }
  };

  // Definicje kafli — kolejność zgodna ze specyfikacją.
  const TABS = [
    { id: "delete", label: "Usuwanie danych",       icon: Trash },
    { id: "stats",  label: "Statystyki",            icon: BarChart3 },
    { id: "mbank",  label: "Playground mBank PSD2", icon: Landmark },
    { id: "xml",    label: "XML / KSeF",            icon: FileCode2 },
    { id: "emergency", label: "Awaryjne odzyskiwanie workflow", icon: Skull },
    { id: "ai-health", label: "AI Health",          icon: HeartPulse },
  ];

  return (
    <div data-testid="dev-tools-page">
      {/* Header — zachowany 1:1 z poprzedniego widoku. */}
      <div className="mb-5">
        <Link
          to="/settings"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-3"
          data-testid="dev-back-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Wróć do ustawień
        </Link>
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-lg bg-gray-100 shrink-0">
            <Wrench className="w-6 h-6 text-gray-700" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-[#0F1F3D] tracking-tight">
              Narzędzia developerskie
            </h1>
            <p className="text-gray-500 mt-0.5 text-sm">
              Panel diagnostyczno-serwisowy PAX. Wybierz sekcję poniżej.
            </p>
          </div>
        </div>
      </div>

      {/* Nawigacja kaflowa — styl 1:1 z Paczek Płatności.
          Mobile: 2×2 (wszystkie 4 widoczne bez scrolla). Desktop: 1×4. */}
      <div
        role="tablist"
        aria-label="Sekcje Narzędzi developerskich"
        data-testid="dev-tabs"
        className="grid grid-cols-2 sm:grid-cols-6 gap-3 mb-4"
      >
        {TABS.map((t) => {
          const active = activeSection === t.id;
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveSection(t.id)}
              role="tab"
              aria-selected={active}
              className={`min-h-[92px] sm:min-h-[56px] rounded-xl border-2 px-4 py-3 text-base font-semibold text-center transition-colors ${
                active
                  ? "border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm"
                  : "border-gray-200 bg-white text-gray-700 hover:border-indigo-300 hover:bg-indigo-50/40"
              }`}
              data-testid={`dev-tab-${t.id}`}
            >
              <div className="flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-2">
                <Icon className="w-5 h-5 shrink-0" />
                <span className="leading-tight">{t.label}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* SEKCJA 1 — Usuwanie danych */}
      {activeSection === "delete" && (
        <DeleteSection
          stats={stats}
          loading={loading}
          busyId={busyId}
          onDelete={handleDelete}
          onRefreshStats={fetchStats}
        />
      )}

      {/* SEKCJA 2 — Statystyki (read-only) */}
      {activeSection === "stats" && (
        <StatsSection stats={stats} loading={loading} onRefresh={fetchStats} />
      )}

      {/* SEKCJA 3 — Playground mBank PSD2 (architektura + placeholdery) */}
      {activeSection === "mbank" && <MBankPlaygroundSection />}

      {/* SEKCJA 4 — XML / KSeF (narzędzia serwisowe) */}
      {activeSection === "xml" && <XmlKsefSection />}

      {/* SEKCJA 5 — Awaryjne odzyskiwanie workflow (Kill Switch) */}
      {activeSection === "emergency" && <KillSwitchSection />}

      {/* SEKCJA 6 — AI Health (kondycja LLM per-agent) */}
      {activeSection === "ai-health" && <AiHealthSection />}
    </div>
  );
}

/** UnlockInBatchCard — DevTools: odblokowanie faktur „W paczce" bez
 * usuwania paczek. Osobna karta (indigo, nie rose) — akcja NAPRAWCZA,
 * nie destrukcyjna. POST /api/dev-tools/invoices/unlock-in-batch.
 * Modal potwierdzenia jasno rozgranicza: faktury zostaną odblokowane,
 * paczki NIE zostaną usunięte.
 */
const UnlockInBatchCard = ({ stats, loading, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const inBatchCount = stats?.invoices_in_batch_count ?? 0;

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/dev-tools/invoices/unlock-in-batch`,
        {},
        { withCredentials: true },
      );
      const unlocked = r.data?.unlocked_invoices_count ?? 0;
      const cnCleared = r.data?.cleared_credit_note_links_count ?? 0;
      if (unlocked === 0 && cnCleared === 0) {
        toast.info("Nie znaleziono faktur oznaczonych jako „W paczce”.");
      } else {
        const parts = [];
        if (unlocked > 0) {
          parts.push(`Odblokowano ${unlocked} ${polishPlural(unlocked, ["fakturę", "faktury", "faktur"])}`);
        }
        if (cnCleared > 0) {
          parts.push(`wyczyszczono ${cnCleared} ${polishPlural(cnCleared, ["blokadę korekty", "blokady korekt", "blokad korekt"])}`);
        }
        toast.success(parts.join(" i ") + ".");
      }
      setOpen(false);
      await onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd odblokowania faktur.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border border-indigo-200 bg-indigo-50/30" data-testid="dev-op-card-unlock-in-batch">
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="p-2 rounded-md shrink-0 bg-indigo-100">
            <Unlock className="w-4 h-4 text-indigo-700" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900">
              Odblokuj faktury „W paczce”
            </div>
            <div className="text-[11px] text-gray-600 break-words mt-0.5">
              Czyści `status=in_batch`, `package_id` i `credit_note_used_in_package`
              — obsługuje też osierocone faktury (`in_batch` bez istniejącej paczki).
            </div>
            <div className="text-[11px] text-emerald-700 break-words mt-0.5">
              Nie usuwa faktur ani paczek. Nie cofa statusów paid/sent/exported.
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          <CountChip
            label="Faktury W paczce"
            value={inBatchCount}
            loading={loading}
          />
        </div>

        <AlertDialog open={open} onOpenChange={setOpen}>
          <AlertDialogTrigger asChild>
            <Button
              disabled={loading || busy}
              className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-gray-300"
              data-testid="dev-op-btn-unlock-in-batch"
            >
              {busy
                ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                : <Unlock className="w-4 h-4 mr-1.5" />}
              Odblokuj faktury „W paczce”
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="dev-unlock-in-batch-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Odblokuj wszystkie faktury „W paczce”?</AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <span className="block">
                  Odblokujesz wszystkie swoje faktury oznaczone jako „W paczce”.
                  Faktury ponownie będą mogły trafić do Agenta Płatności.
                </span>
                <span className="block font-medium">
                  Istniejące paczki nie zostaną usunięte.
                </span>
                <span className="block text-xs text-gray-600">
                  Tej operacji używaj wyłącznie do naprawy lub testów.
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="dev-unlock-in-batch-cancel">
                Anuluj
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirm}
                disabled={busy}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="dev-unlock-in-batch-confirm"
              >
                {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                Odblokuj faktury
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};

/** DeleteSection — grupuje wszystkie akcje resetujące. Logika bez zmian. */
const DeleteSection = ({ stats, loading, busyId, onDelete, onRefreshStats }) => {
  // Grupowanie operacji według section (stary algorytm, przeniesiony 1:1).
  const sections = [];
  const seen = new Set();
  for (const op of OPERATIONS) {
    if (!seen.has(op.section)) {
      sections.push({
        section: op.section,
        sectionLabel: op.sectionLabel,
        sectionIcon: op.sectionIcon,
        sectionColor: op.sectionColor,
        ops: [op],
      });
      seen.add(op.section);
    } else {
      sections.find((s) => s.section === op.section).ops.push(op);
    }
  }
  return (
    <div data-testid="dev-section-delete">
      <div className="mb-4 bg-rose-50 border border-rose-200 rounded p-3 text-xs text-rose-900 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          Operacje w tej sekcji usuwają dane <strong>nieodwracalnie</strong> i wykonują się{" "}
          <strong>natychmiast po kliknięciu</strong>. Używaj wyłącznie podczas developmentu/testów.
          Konfiguracja KSeF, ustawienia agentów AI oraz konta użytkowników <strong>nie</strong> są usuwane.
        </span>
      </div>
      <div className="grid gap-5">
        {sections.map((sec) => {
          const SecIcon = sec.sectionIcon;
          const color = COLOR_MAP[sec.sectionColor] || COLOR_MAP.blue;
          return (
            <div key={sec.section} data-testid={`dev-section-${sec.section}`}>
              <div className="flex items-center gap-2 mb-2.5">
                <SecIcon className={`w-4 h-4 ${color.text}`} />
                <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                  {sec.sectionLabel}
                </h2>
              </div>
              <div className="grid gap-3">
                {sec.ops.map((op) => (
                  <OperationCard
                    key={op.id}
                    op={op}
                    stats={stats}
                    loading={loading}
                    busyId={busyId}
                    onDelete={onDelete}
                  />
                ))}
                {sec.section === "paczki" && (
                  <UnlockInBatchCard
                    stats={stats}
                    loading={loading}
                    onDone={onRefreshStats}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** StatsSection — wszystkie liczniki + status KSeF + status mBank + AI Profiles viewer. */
const StatsSection = ({ stats, loading, onRefresh }) => {
  const [ksefStatus, setKsefStatus] = useState(null);
  const [ksefLoading, setKsefLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setKsefLoading(true);
      try {
        const r = await axios.get(`${API}/ksef/settings/status`, { withCredentials: true });
        if (!cancelled) setKsefStatus(r.data || {});
      } catch {
        if (!cancelled) setKsefStatus(null);
      } finally {
        if (!cancelled) setKsefLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Grupy liczników — mapowanie kluczy z `/api/dev-tools/stats` na czytelne labele.
  const groups = [
    {
      label: "Faktury",
      icon: FileText,
      color: "blue",
      items: [
        { key: "invoices_count",           label: "Faktury w bazie" },
        { key: "ksef_invoices_count",      label: "Faktury KSeF" },
        { key: "invoice_profiles_count",   label: "Profile Agenta Faktur" },
        { key: "invoice_agent_memory_count", label: "Pamięć Agenta Faktur" },
      ],
    },
    {
      label: "Wyciągi bankowe",
      icon: Wallet,
      color: "emerald",
      items: [
        { key: "bank_transactions_count", label: "Transakcje bankowe" },
        { key: "bank_imports_count",      label: "Importy wyciągów" },
        { key: "bank_profiles_count",     label: "Profile Agenta Wyciągów" },
      ],
    },
    {
      label: "Paczki płatności",
      icon: Package,
      color: "indigo",
      items: [
        { key: "payment_packages_count",  label: "Paczki (draft)" },
        { key: "payment_proposals_count", label: "Propozycje AI" },
        { key: "payment_analyses_count",  label: "Łącznie analiz" },
        { key: "matching_count",          label: "Dopasowania Bank↔Faktura" },
      ],
    },
  ];

  return (
    <div className="space-y-5" data-testid="dev-section-stats">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          Read-only migawka stanu bazy. Dane per zalogowany użytkownik.
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={loading}
          data-testid="dev-stats-refresh-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
          Odśwież
        </Button>
      </div>

      {groups.map((g) => {
        const Icon = g.icon;
        const color = COLOR_MAP[g.color] || COLOR_MAP.blue;
        return (
          <div key={g.label} data-testid={`dev-stats-group-${g.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center gap-2 mb-2.5">
              <Icon className={`w-4 h-4 ${color.text}`} />
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                {g.label}
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {g.items.map((it) => (
                <StatTile
                  key={it.key}
                  label={it.label}
                  value={stats?.[it.key]}
                  loading={loading}
                  testId={`dev-stat-${it.key}`}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* Status KSeF */}
      <div data-testid="dev-stats-group-ksef">
        <div className="flex items-center gap-2 mb-2.5">
          <FileCode2 className="w-4 h-4 text-indigo-600" />
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
            Konfiguracja i status KSeF
          </h2>
        </div>
        <Card className="border border-gray-200">
          <CardContent className="p-3 sm:p-4">
            {ksefLoading ? (
              <div className="text-xs text-gray-500 flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Ładowanie statusu KSeF…
              </div>
            ) : ksefStatus ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                <InfoRow label="NIP firmy"        value={ksefStatus.company_nip || "—"} />
                <InfoRow label="Środowisko"       value={ksefStatus.environment || "—"} />
                <InfoRow label="Status"           value={ksefStatus.status || "—"} />
                <InfoRow label="Certyfikat mTLS"  value={ksefStatus.has_cert ? "tak" : "nie"} />
                <InfoRow label="Faktury zsynchr." value={ksefStatus.invoices_synced ?? "—"} />
                <InfoRow label="Ostatni sync"     value={fmtDate(ksefStatus.last_sync_at)} />
              </div>
            ) : (
              <div className="text-xs text-gray-500">Brak danych statusu KSeF.</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Status mBank — na razie placeholder (endpoint nie istnieje) */}
      <div data-testid="dev-stats-group-mbank">
        <div className="flex items-center gap-2 mb-2.5">
          <Landmark className="w-4 h-4 text-amber-600" />
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
            Status mBank PSD2
          </h2>
        </div>
        <PlaceholderCard testId="dev-stats-mbank-placeholder">
          Integracja mBank PSD2 XS2A v2 jest w przygotowaniu. Diagnostyka dostępna będzie
          w sekcji „Playground mBank PSD2” po podpięciu endpointów backendu.
        </PlaceholderCard>
      </div>

      {/* Creator Lock (Closed Beta Mode) — diagnostyka bramki dostępu */}
      <CreatorLockDiagnostics />

      {/* Demo Dataset P1-lite — load/reset/status */}
      <DemoDatasetPanel />

      {/* Demo Mode P0 — status + izolacja danych */}
      <DemoModeDiagnostics />

      {/* Sync profili faktur ↔ Agent Wyciągów (deterministyczny background job) */}
      <ProfileSyncPanel />

      {/* Payment Agent Stats — obserwabilność LLM (2026-07 P1 finish) */}
      <PaymentAgentStatsPanel />

      {/* Profile AI viewer — read-only diagnostyka. Zachowany 1:1 z poprzedniej wersji. */}
      <div data-testid="dev-ai-profiles-section">
        <div className="flex items-center gap-2 mb-2.5">
          <Sparkles className="w-4 h-4 text-indigo-700" />
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
            Profile AI (diagnostyka)
          </h2>
        </div>
        <AiProfilesViewer />
      </div>
    </div>
  );
};

/** DemoDatasetPanel — Demo Dataset P1-lite dla admina.
 *  Endpointy: GET/POST /api/demo-dataset/{status,load,reset,reset-and-load}
 *  Wszystkie modyfikacje wymagają potwierdzenia w window.confirm z
 *  jasnym komunikatem "Operacja dotyczy wyłącznie bazy Demo".
 */
const DemoDatasetPanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // "load" | "reset" | "reset-and-load"
  const [lastReport, setLastReport] = useState(null);

  const fetchStatus = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await axios.get(`${API}/demo-dataset/status`, { withCredentials: true });
      setStatus(r.data);
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Błąd pobierania statusu datasetu.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const runOp = async (op, confirmMsg) => {
    if (!window.confirm(confirmMsg)) return;
    setBusy(op);
    setLastReport(null);
    try {
      const r = await axios.post(`${API}/demo-dataset/${op}`, {}, { withCredentials: true });
      setLastReport(r.data);
      toast.success(
        op === "load" ? "Dataset załadowany do Demo DB."
        : op === "reset" ? "Demo DB wyczyszczona."
        : "Demo DB zresetowana i załadowana od nowa."
      );
      await fetchStatus(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Błąd operacji ${op}.`);
    } finally {
      setBusy(null);
    }
  };

  const isDemoActive = status?.active_mode === "demo";
  const dsLoaded = status?.dataset_loaded;

  return (
    <div data-testid="dev-demo-dataset-section">
      <div className="flex items-center gap-2 mb-2.5">
        <Database className="w-4 h-4 text-amber-700" />
        <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
          Demo Dataset (P1-lite)
        </h2>
        {dsLoaded ? (
          <span className="ml-auto text-[10px] uppercase tracking-wide text-amber-800 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5 font-semibold" data-testid="dev-demo-dataset-loaded-badge">
            {status?.dataset_version || "loaded"}
          </span>
        ) : (
          <span className="ml-auto text-[10px] uppercase tracking-wide text-gray-500 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5" data-testid="dev-demo-dataset-empty-badge">
            Pusto
          </span>
        )}
      </div>

      <Card className={`border ${dsLoaded ? "border-amber-200" : "border-gray-200"}`} data-testid="dev-demo-dataset-card">
        <CardContent className="p-3 sm:p-4 space-y-3">
          <div className="text-[11px] text-gray-600 leading-snug">
            Realistyczny zestaw fake danych do nagrań TikTok/YouTube i prezentacji inwestorom:
            25 faktur (15 pending / 5 overdue / 5 paid), 2 wyciągi (15 transakcji),
            8 kontrahentów (2 z <code className="font-mono text-[10px]">needs_review</code>),
            8 profili fakturowych + 8 bankowych, 5 paczek (2 draft, 2 propozycje Payment Agent, 1 exported).
            Wszystkie operacje dotyczą <span className="font-semibold text-amber-800">wyłącznie Demo DB</span>{" "}
            (<code className="font-mono text-[10px]">{status?.demo_db_name || "—"}</code>).
            Production pozostanie nietknięte.
          </div>

          {!isDemoActive && (
            <div className="rounded border border-blue-200 bg-blue-50 p-2 text-[11px] text-blue-900" data-testid="dev-demo-dataset-hint-switch">
              Jesteś w trybie <span className="font-semibold">Production</span>. Aby zobaczyć fake dane w widokach aplikacji,
              przełącz się w Demo Mode w menu avatara.
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <InfoRow label="Wersja datasetu" value={status?.dataset_version || "—"} />
            <InfoRow label="Załadowany" value={fmtDate(status?.loaded_at)} />
            <InfoRow label="Przez" value={status?.loaded_by || "—"} />
            <InfoRow label="Aktywny tryb" value={isDemoActive ? "DEMO" : "PRODUCTION"} />
          </div>

          <div className="rounded border border-gray-200 bg-gray-50 p-2" data-testid="dev-demo-dataset-counts">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">
              Liczby rekordów w Demo DB
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-[11px]">
              {(() => {
                // Deterministyczna kolejność + polskie etykiety (bez tłumaczenia
                // API — mapa jest tylko w warstwie prezentacji, `status.counts`
                // zawiera surowe nazwy kolekcji z backendu).
                const LABELS = [
                  ["invoices", "Faktury"],
                  ["bank_imports", "Wyciągi (importy)"],
                  ["bank_transactions", "Transakcje wyciągów"],
                  ["contractors", "Kontrahenci"],
                  ["vendor_invoice_profiles", "Profile fakturowe"],
                  ["payment_counterparty_profiles", "Profile bankowe"],
                  ["payment_packages", "Paczki płatności"],
                  ["payment_files", "Pliki eksportu"],
                ];
                const c = status?.counts || {};
                return LABELS.map(([key, label]) => (
                  <div
                    key={key}
                    className="flex justify-between items-baseline gap-1.5 py-0.5"
                    data-testid={`dev-demo-count-${key}`}
                  >
                    <span className="text-gray-700 text-[10px]">{label}</span>
                    <span className="text-gray-900 font-semibold font-mono text-[11px]">
                      {c[key] ?? 0}
                    </span>
                  </div>
                ));
              })()}
            </div>
          </div>

          {lastReport && (
            <div className="rounded border border-emerald-200 bg-emerald-50 p-2 text-[11px] text-emerald-900" data-testid="dev-demo-dataset-report">
              <div className="font-semibold text-[10px] uppercase tracking-wide mb-1">
                Ostatnia operacja: {lastReport.operation}
              </div>
              <pre className="font-mono text-[10px] overflow-x-auto">
                {JSON.stringify(lastReport, null, 2)}
              </pre>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
            <Button
              onClick={() => runOp("load",
                "Załaduj Demo Dataset?\n\n" +
                "Operacja dotyczy wyłącznie bazy Demo. Production nie zostanie zmienione.\n\n" +
                "Load jest idempotentny — istniejące demo rekordy zostaną nadpisane."
              )}
              disabled={busy !== null}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="dev-demo-dataset-load-btn"
            >
              {busy === "load" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Play className="w-4 h-4 mr-1.5" />}
              Załaduj Demo Dataset
            </Button>
            <Button
              onClick={() => runOp("reset",
                "Wyczyścić Demo DB?\n\n" +
                "Operacja dotyczy wyłącznie bazy Demo. Production nie zostanie zmienione.\n\n" +
                "Usunięte zostaną WSZYSTKIE fake rekordy z Demo DB (faktury, wyciągi, profile, paczki, metadata)."
              )}
              disabled={busy !== null || !dsLoaded}
              variant="outline"
              className="border-rose-300 text-rose-700 hover:bg-rose-50"
              data-testid="dev-demo-dataset-reset-btn"
            >
              {busy === "reset" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
              Wyczyść Demo
            </Button>
            <Button
              onClick={() => runOp("reset-and-load",
                "Reset + Załaduj Demo Dataset?\n\n" +
                "Operacja dotyczy wyłącznie bazy Demo. Production nie zostanie zmienione.\n\n" +
                "1) Demo DB zostanie wyczyszczona\n" +
                "2) Świeży dataset zostanie załadowany"
              )}
              disabled={busy !== null}
              variant="outline"
              data-testid="dev-demo-dataset-reset-and-load-btn"
            >
              {busy === "reset-and-load" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
              Reset + Załaduj
            </Button>
            <Button variant="outline" onClick={() => fetchStatus()} disabled={loading || busy !== null} data-testid="dev-demo-dataset-refresh-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
              Odśwież
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};


/** DemoModeDiagnostics — read-only panel Demo Mode dla admina.
 *  Endpoint: `GET /api/demo-mode/diagnostics` (admin only).
 *  Pokazuje: tryb usera, active DB, liczby rekordów w PROD i DEMO,
 *  konfiguracja globalna (ENV) i lista zablokowanych ścieżek w Demo.
 */
const DemoModeDiagnostics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await axios.get(`${API}/demo-mode/diagnostics`, {
        withCredentials: true,
      });
      setData(r.data || null);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Nie udało się pobrać diagnostyki.");
      setData(null);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const cfg = data?.config || {};
  const cu = data?.current_user || {};
  const active = data?.active_db || {};
  const counts = data?.counts || {};
  const isDemo = active?.resolves_to === "demo";

  return (
    <div data-testid="dev-demo-mode-section">
      <div className="flex items-center gap-2 mb-2.5">
        <Sparkles className="w-4 h-4 text-amber-700" />
        <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
          Demo Mode (P0 fundament)
        </h2>
        {cfg.demo_enabled_globally ? (
          <span
            className="ml-auto text-[10px] uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"
            data-testid="dev-demo-mode-enabled-badge"
          >
            ENV: ENABLED
          </span>
        ) : (
          <span
            className="ml-auto text-[10px] uppercase tracking-wide text-gray-500 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5"
            data-testid="dev-demo-mode-disabled-badge"
          >
            ENV: DISABLED
          </span>
        )}
      </div>

      <Card
        className={`border ${isDemo ? "border-amber-200" : "border-gray-200"}`}
        data-testid="dev-demo-mode-card"
      >
        <CardContent className="p-3 sm:p-4 space-y-3">
          <div className="text-[11px] text-gray-600 leading-snug">
            Demo Mode P0 — twarda izolacja przez osobną bazę MongoDB. Wybór
            aktywnej bazy per request przez <code className="font-mono text-[10px]">ContextVar</code>
            (middleware <code className="font-mono text-[10px]">demo_mode_context</code>).
            <span className="font-semibold text-amber-800"> KSeF zawsze wyłączony w Demo</span>
            {" "}(guard w middleware).
          </div>

          {error && (
            <div className="rounded border border-rose-200 bg-rose-50 p-2 text-[11px] text-rose-900">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <InfoRow label="Twój tryb" value={cu.demo_mode ? "DEMO" : "PRODUCTION"} />
            <InfoRow label="Aktywna baza" value={active.name || "—"} />
            <InfoRow label="PROD DB" value={cfg.prod_db_name || "—"} />
            <InfoRow label="DEMO DB" value={cfg.demo_db_name || "—"} />
          </div>

          {/* Liczby rekordów per baza — potwierdzenie izolacji */}
          <div
            className="rounded border border-gray-200 bg-gray-50 p-2"
            data-testid="dev-demo-mode-counts"
          >
            <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">
              Liczby rekordów per baza
            </div>
            <div className="grid grid-cols-2 gap-4 text-[11px]">
              <div>
                <div className="text-[10px] uppercase text-emerald-700 font-semibold mb-1">
                  PRODUCTION
                </div>
                {Object.entries(counts.production || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between font-mono text-[10px] py-0.5">
                    <span className="text-gray-700">{k}</span>
                    <span className="text-gray-900">{v}</span>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-[10px] uppercase text-amber-700 font-semibold mb-1">
                  DEMO
                </div>
                {Object.keys(counts.demo || {}).length === 0 ? (
                  <div className="text-[10px] text-gray-500 italic">Demo DB niedostępne (ENV disabled)</div>
                ) : (
                  Object.entries(counts.demo || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between font-mono text-[10px] py-0.5">
                      <span className="text-gray-700">{k}</span>
                      <span className="text-gray-900">{v}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {data?.blocked_paths_in_demo && (
            <div className="rounded border border-gray-200 bg-gray-50 p-2 text-[11px]">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">
                Ścieżki zablokowane w Demo
              </div>
              <div className="flex flex-wrap gap-1.5">
                {data.blocked_paths_in_demo.map((p) => (
                  <code
                    key={p}
                    className="text-[10px] font-mono bg-amber-100 border border-amber-300 text-amber-900 px-1.5 py-0.5 rounded"
                  >
                    {p}
                  </code>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
            <Button
              variant="outline"
              onClick={() => fetchData()}
              disabled={loading}
              data-testid="dev-demo-mode-refresh-btn"
            >
              {loading
                ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                : <RefreshCw className="w-4 h-4 mr-1.5" />}
              Odśwież
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};


/** CreatorLockDiagnostics — read-only panel z pełnym stanem Creator Lock.
 *  Widoczny tylko dla admina (endpoint zwraca 403 dla nie-admina).
 *  Odczyt z `GET /api/creator-lock/diagnostics`.
 */
const CreatorLockDiagnostics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await axios.get(`${API}/creator-lock/diagnostics`, {
        withCredentials: true,
      });
      setData(r.data || null);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Nie udało się pobrać diagnostyki.");
      setData(null);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const t = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(t);
  }, [fetchData]);

  const lock = data?.lock || {};
  const cu = data?.current_user || {};
  const mw = data?.middleware || {};
  const cfg = data?.config || {};
  const lockOn = lock.enabled === true;

  return (
    <div data-testid="dev-creator-lock-section">
      <div className="flex items-center gap-2 mb-2.5">
        <Lock className="w-4 h-4 text-amber-700" />
        <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
          Creator Lock (Closed Beta)
        </h2>
        {lockOn ? (
          <span
            className="ml-auto text-[10px] uppercase tracking-wide text-amber-800 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5 font-semibold"
            data-testid="dev-creator-lock-on-badge"
          >
            ON
          </span>
        ) : (
          <span
            className="ml-auto text-[10px] uppercase tracking-wide text-gray-500 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5"
            data-testid="dev-creator-lock-off-badge"
          >
            OFF
          </span>
        )}
      </div>

      <Card
        className={`border ${lockOn ? "border-amber-200" : "border-gray-200"}`}
        data-testid="dev-creator-lock-card"
      >
        <CardContent className="p-3 sm:p-4 space-y-3">
          <div className="text-[11px] text-gray-600 leading-snug">
            Globalna bramka dostępu do PAX. Blokuje publiczne logowanie gdy ON;
            administrator (email w <code className="font-mono text-[10px]">ADMIN_EMAILS</code>)
            zawsze może wejść. Cache 3 s — zmiana widoczna natychmiast, bez restartu.
          </div>

          {error && (
            <div className="rounded border border-rose-200 bg-rose-50 p-2 text-[11px] text-rose-900">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <InfoRow label="Status" value={lockOn ? "ON — dostęp zamknięty" : "OFF — publiczny"} />
            <InfoRow label="Ostatnia zmiana" value={fmtDate(lock.updated_at)} />
            <InfoRow label="Zmienione przez" value={lock.updated_by || "—"} />
            <InfoRow label="Emergency Admin (ENV)" value={cfg.emergency_admin_env ? "ON" : "OFF"} />
          </div>

          <div
            className="rounded border border-gray-200 bg-gray-50 p-2"
            data-testid="dev-creator-lock-current-user"
          >
            <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">
              Aktualny użytkownik
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
              <InfoRow label="Email" value={cu.email || "—"} />
              <InfoRow label="Rola" value={cu.role || "—"} />
              <InfoRow label="Admin?" value={cu.is_admin ? "TAK" : "nie"} />
              <InfoRow label="Zalogowany?" value={cu.is_authenticated ? "TAK" : "nie"} />
            </div>
          </div>

          <div
            className="rounded border border-gray-200 bg-gray-50 p-2"
            data-testid="dev-creator-lock-middleware"
          >
            <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">
              Bramka middleware
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <InfoRow label="Przepuszcza aktualny request?" value={mw.would_pass ? "TAK" : "NIE"} />
              <InfoRow label="Powód" value={mw.reason || "—"} />
              <InfoRow label="Skonfigurowanych adminów" value={cfg.admin_emails_configured_count ?? "—"} />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
            <Button
              variant="outline"
              onClick={() => fetchData()}
              disabled={loading}
              data-testid="dev-creator-lock-refresh-btn"
            >
              {loading
                ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                : <RefreshCw className="w-4 h-4 mr-1.5" />}
              Odśwież
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};


/** ProfileSyncPanel — status i manual trigger deterministycznego background jobu
 *  syncującego `vendor_invoice_profiles` z `payment_counterparty_profiles`.
 *
 *  Backend endpointy (bez LLM, patrz `services/profile_sync_job_runner.py`):
 *    GET  /api/ai/agents/invoice/profile-sync/status
 *    POST /api/ai/agents/invoice/profile-sync/run-once
 *
 *  UX: read-only status + jeden przycisk „Uruchom teraz". Auto-refresh co 15s.
 *  Odpowiedź `run-once` może mieć `status: "busy"` — obsługiwane osobnym toastem.
 */
const ProfileSyncPanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [lastManualCounts, setLastManualCounts] = useState(null);

  const fetchStatus = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await axios.get(
        `${API}/ai/agents/invoice/profile-sync/status`,
        { withCredentials: true },
      );
      setStatus(r.data || {});
    } catch (e) {
      if (!silent) {
        toast.error(e?.response?.data?.detail || "Błąd pobierania statusu sync profili.");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    // Auto-refresh co 15s — tylko dopóki panel widoczny.
    const t = setInterval(() => fetchStatus(true), 15000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const handleRunOnce = async () => {
    setRunning(true);
    setLastManualCounts(null);
    try {
      const r = await axios.post(
        `${API}/ai/agents/invoice/profile-sync/run-once`,
        {},
        { withCredentials: true },
      );
      const data = r.data || {};
      if (data.status === "busy") {
        toast.message(data.message || "Poprzedni tick jeszcze trwa — spróbuj za chwilę.");
      } else if (data.status === "ok") {
        const c = data.counts || {};
        setLastManualCounts(c);
        const synced = c.synced ?? 0;
        toast.success(
          synced > 0
            ? `Zsynchronizowano ${synced} ${polishPlural(synced, ["profil", "profile", "profili"])}.`
            : "Tick zakończony — brak profili do zmiany.",
        );
      } else {
        toast.message("Tick zakończony.");
      }
      await fetchStatus(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd uruchamiania sync.");
    } finally {
      setRunning(false);
    }
  };

  const isEnabled = status?.enabled === true;
  const isRunning = status?.is_running === true;
  const lastResult = status?.last_result || null;
  const lastError = status?.last_error;

  return (
    <div data-testid="dev-profile-sync-section">
      <div className="flex items-center gap-2 mb-2.5">
        <Activity className="w-4 h-4 text-emerald-700" />
        <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
          Sync profili faktur ↔ Agent Wyciągów
        </h2>
        {isEnabled ? (
          <span
            className="ml-auto text-[10px] uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"
            data-testid="dev-profile-sync-enabled-badge"
          >
            Aktywny
          </span>
        ) : (
          <span
            className="ml-auto text-[10px] uppercase tracking-wide text-gray-500 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5"
            data-testid="dev-profile-sync-disabled-badge"
          >
            Wyłączony (ENV)
          </span>
        )}
      </div>

      <Card className="border border-emerald-200" data-testid="dev-profile-sync-card">
        <CardContent className="p-3 sm:p-4 space-y-3">
          <div className="text-[11px] text-gray-600 leading-snug">
            Deterministyczny background job (bez LLM) uzupełnia{" "}
            <code className="font-mono text-[10px]">payment_method</code> w{" "}
            <code className="font-mono text-[10px]">vendor_invoice_profiles</code> na
            podstawie decyzji Agenta Wyciągów w{" "}
            <code className="font-mono text-[10px]">payment_counterparty_profiles</code>.
            Interval:{" "}
            <span className="font-semibold text-gray-800">
              {status?.interval_minutes ?? "—"} min
            </span>
            . Kill-switch: <code className="font-mono text-[10px]">PROFILE_SYNC_ENABLED=false</code>.
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <InfoRow
              label="Status"
              value={
                loading
                  ? "…"
                  : isRunning
                    ? "Tick w toku"
                    : isEnabled ? "Oczekuje" : "Wyłączony"
              }
            />
            <InfoRow label="Liczba tickow" value={status?.tick_count ?? "—"} />
            <InfoRow
              label="Stale profile (ostatnio)"
              value={status?.stale_count_last_seen ?? "—"}
            />
            <InfoRow label="Następny tick" value={fmtDate(status?.next_run_at)} />
            <InfoRow label="Ostatni start" value={fmtDate(status?.last_started_at)} />
            <InfoRow label="Ostatni koniec" value={fmtDate(status?.last_finished_at)} />
          </div>

          {lastResult && (
            <div
              className="rounded border border-gray-200 bg-gray-50 p-2"
              data-testid="dev-profile-sync-last-result"
            >
              <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1.5">
                Ostatni wynik ticka
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 text-[11px]">
                <ResultStat label="synced" value={lastResult.synced} tone="ok" />
                <ResultStat label="no_change" value={lastResult.no_change} />
                <ResultStat label="manual_override" value={lastResult.manual_override} />
                <ResultStat label="profile_missing" value={lastResult.profile_missing} />
                <ResultStat label="bank_agent_unknown" value={lastResult.bank_agent_unknown} />
                <ResultStat
                  label="errors"
                  value={lastResult.errors}
                  tone={(lastResult.errors ?? 0) > 0 ? "warn" : "ok"}
                />
              </div>
            </div>
          )}

          {lastError && (
            <div
              className="rounded border border-rose-200 bg-rose-50 p-2 text-[11px] text-rose-900"
              data-testid="dev-profile-sync-error"
            >
              <div className="font-semibold mb-0.5">Ostatni błąd</div>
              <div className="font-mono break-all">{lastError}</div>
            </div>
          )}

          {lastManualCounts && !lastResult && (
            <div
              className="rounded border border-emerald-200 bg-emerald-50 p-2 text-[11px] text-emerald-900"
              data-testid="dev-profile-sync-manual-result"
            >
              Manualny tick zakończony. Synced:{" "}
              <span className="font-mono font-semibold">{lastManualCounts.synced ?? 0}</span>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
            <Button
              onClick={handleRunOnce}
              disabled={running || !isEnabled}
              className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-gray-300"
              data-testid="dev-profile-sync-run-btn"
            >
              {running
                ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                : <Play className="w-4 h-4 mr-1.5" />}
              {running ? "Uruchamiam…" : "Uruchom teraz"}
            </Button>
            <Button
              variant="outline"
              onClick={() => fetchStatus()}
              disabled={loading}
              data-testid="dev-profile-sync-refresh-btn"
            >
              {loading
                ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                : <RefreshCw className="w-4 h-4 mr-1.5" />}
              Odśwież
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/** PaymentAgentStatsPanel — obserwabilność LLM dla Agenta Płatności (2026-07).
 *  Endpoint: GET /api/dev-tools/ai-profiles/payment-agent/stats
 *  Dane: `ai_provider_logs` (interceptor `log_provider_call`).
 *
 *  Wyświetla:
 *   - Summary: total_calls, success/error, avg/max/p95 latency, tokeny,
 *     koszt, ostatni provider/model.
 *   - Recent 20: data, status, latency, tokens, cost, provider/model,
 *     error_type, retry_count, finish_reason.
 *
 *  Auto-refresh co 30s (płynny UX, ale nie generuje ruchu bez potrzeby).
 *  Zero zmian w Agent Płatności / logice / promptach.
 */
const PaymentAgentStatsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStats = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const r = await axios.get(
        `${API}/dev-tools/ai-profiles/payment-agent/stats?limit=20`,
        { withCredentials: true },
      );
      setData(r.data || null);
    } catch (e) {
      if (!silent) {
        toast.error(e?.response?.data?.detail || "Błąd pobierania statystyk Payment Agent.");
      }
    } finally {
      if (!silent) setLoading(false);
      else setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    // Auto-refresh co 30s — panel jest lekki (agregacja Mongo).
    const t = setInterval(() => fetchStats(true), 30000);
    return () => clearInterval(t);
  }, [fetchStats]);

  const s = data?.summary || null;
  const recent = data?.recent || [];

  const fmtMs = (v) => {
    if (v == null) return "—";
    if (v < 1000) return `${Math.round(v)} ms`;
    return `${(v / 1000).toFixed(1)} s`;
  };
  const fmtCost = (v) => {
    if (v == null || v === 0) return "$0";
    if (v < 0.001) return `$${(v * 1000).toFixed(3)}m`; // millidollars
    return `$${v.toFixed(4)}`;
  };
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("pl-PL", {
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div data-testid="dev-payment-agent-stats-section">
      <div className="flex items-center gap-2 mb-2.5">
        <Activity className="w-4 h-4 text-purple-700" />
        <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
          Payment Agent — statystyki LLM
        </h2>
        <button
          onClick={() => fetchStats(false)}
          disabled={loading}
          className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
          data-testid="dev-payment-agent-stats-refresh"
          title="Odśwież statystyki"
        >
          <RefreshCw className={`w-3 h-3 ${(loading || refreshing) ? "animate-spin" : ""}`} />
          Odśwież
        </button>
      </div>

      {loading && !data ? (
        <div className="text-xs text-gray-500 flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Ładowanie…
        </div>
      ) : (
        <div className="space-y-3">
          {/* Nota interpretacyjna (help text) */}
          <div className="text-[11px] text-gray-600 bg-purple-50 border border-purple-200 rounded p-2 leading-snug">
            Statystyki pokazują <strong>rzeczywisty czas odpowiedzi providera LLM</strong> dla Agenta
            Płatności. Po usunięciu lokalnego timeoutu PAX (2026-07) analiza może trwać
            <strong> dłużej niż 60 sekund</strong>. Źródło: kolekcja <code className="font-mono text-[10px]">ai_provider_logs</code>{" "}
            (filtr <code className="font-mono text-[10px]">agent_id=payment_agent</code>, TTL 90 dni).
          </div>

          {/* Summary grid — mobile 2 kol, desktop 4-6 kol */}
          {s && s.total_calls === 0 ? (
            <div
              className="text-xs text-gray-500 italic p-3 bg-gray-50 border border-gray-200 rounded"
              data-testid="dev-payment-agent-stats-empty"
            >
              Brak zarejestrowanych wywołań Agenta Płatności. Statystyki pojawią się po pierwszej
              analizie kontrahenta w Payment Agent.
            </div>
          ) : s ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                <StatCard label="Wywołania" value={s.total_calls} testId="stat-total-calls" />
                <StatCard
                  label="Sukcesy"
                  value={s.success_count}
                  className="text-emerald-700"
                  testId="stat-success"
                />
                <StatCard
                  label="Błędy"
                  value={s.error_count}
                  className={s.error_count > 0 ? "text-red-700" : "text-gray-600"}
                  testId="stat-errors"
                />
                <StatCard label="Śr. czas LLM" value={fmtMs(s.avg_latency_ms)} testId="stat-avg-latency" />
                <StatCard label="Max czas LLM" value={fmtMs(s.max_latency_ms)} testId="stat-max-latency" />
                <StatCard label="p95 czas LLM" value={fmtMs(s.p95_latency_ms)} testId="stat-p95-latency" />
                <StatCard
                  label="Tokeny łącznie"
                  value={(s.total_tokens || 0).toLocaleString("pl-PL")}
                  testId="stat-total-tokens"
                />
                <StatCard
                  label="Input tokens"
                  value={(s.total_input_tokens || 0).toLocaleString("pl-PL")}
                  testId="stat-input-tokens"
                />
                <StatCard
                  label="Output tokens"
                  value={(s.total_output_tokens || 0).toLocaleString("pl-PL")}
                  testId="stat-output-tokens"
                />
                <StatCard label="Koszt łącznie" value={fmtCost(s.total_estimated_cost_usd)} testId="stat-total-cost" />
                <StatCard
                  label="Ostatni provider"
                  value={s.last_provider ? `${s.last_provider}` : "—"}
                  className="text-[10px]"
                  testId="stat-last-provider"
                />
                <StatCard
                  label="Ostatni model"
                  value={s.last_model ? s.last_model.replace(/-2025\d+$/, "") : "—"}
                  className="text-[10px] break-all"
                  testId="stat-last-model"
                />
              </div>

              {s.last_call_at && (
                <div className="text-[11px] text-gray-500">
                  Ostatnie wywołanie:{" "}
                  <span className="font-mono text-gray-700">{fmtDate(s.last_call_at)}</span>
                </div>
              )}

              {Object.keys(s.error_types_breakdown || {}).length > 0 && (
                <div className="text-[11px] flex flex-wrap gap-1.5 items-center">
                  <span className="text-gray-600">Podział błędów:</span>
                  {Object.entries(s.error_types_breakdown).map(([k, v]) => (
                    <span
                      key={k}
                      className="inline-flex items-center px-1.5 py-0.5 rounded border border-red-200 bg-red-50 text-red-700 font-mono text-[10px]"
                      data-testid={`stat-error-type-${k}`}
                    >
                      {k}: {v}
                    </span>
                  ))}
                </div>
              )}

              {/* Recent table — mobile: cards, desktop: table */}
              <div className="mt-3">
                <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1.5">
                  Ostatnie {recent.length} analiz
                </div>
                <div className="border border-gray-200 rounded overflow-hidden">
                  {/* Desktop table */}
                  <div className="hidden md:block">
                    <table className="w-full text-[11px]">
                      <thead className="bg-gray-50 text-gray-600 uppercase tracking-wide">
                        <tr>
                          <th className="text-left px-2 py-1.5 font-medium">Data</th>
                          <th className="text-left px-2 py-1.5 font-medium">Status</th>
                          <th className="text-right px-2 py-1.5 font-medium">Czas</th>
                          <th className="text-right px-2 py-1.5 font-medium">Tokeny</th>
                          <th className="text-right px-2 py-1.5 font-medium">Koszt</th>
                          <th className="text-left px-2 py-1.5 font-medium">Provider/Model</th>
                          <th className="text-left px-2 py-1.5 font-medium">Błąd / Finish</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recent.map((r) => (
                          <tr
                            key={r._id}
                            className="border-t border-gray-100"
                            data-testid={`stat-row-${r._id}`}
                          >
                            <td className="px-2 py-1.5 font-mono text-[10px]">{fmtDate(r.created_at)}</td>
                            <td className="px-2 py-1.5">
                              <StatusBadge status={r.status} />
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono">{fmtMs(r.latency_ms)}</td>
                            <td className="px-2 py-1.5 text-right font-mono">
                              {(r.tokens?.total || 0).toLocaleString("pl-PL")}
                              <span className="text-gray-400 text-[9px] ml-1">
                                ({r.tokens?.input || 0}/{r.tokens?.output || 0})
                              </span>
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono">{fmtCost(r.estimated_cost_usd)}</td>
                            <td className="px-2 py-1.5 text-[10px]">
                              <span className="text-gray-500">{r.provider}</span>{" "}
                              <span className="text-gray-700">
                                {r.model?.replace(/-2025\d+$/, "") || "—"}
                              </span>
                              {(r.retry_count || 0) > 0 && (
                                <span className="ml-1 text-[9px] text-amber-700">
                                  retry {r.retry_count}
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-1.5 text-[10px]">
                              {r.status === "error" ? (
                                <span className="text-red-700">
                                  {r.error_type && (
                                    <span className="font-mono">[{r.error_type}] </span>
                                  )}
                                  {r.error_message ? r.error_message.slice(0, 60) + (r.error_message.length > 60 ? "…" : "") : "—"}
                                </span>
                              ) : (
                                <span className="text-gray-500 font-mono">{r.finish_reason || "—"}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile cards */}
                  <div className="md:hidden divide-y divide-gray-100">
                    {recent.map((r) => (
                      <div key={r._id} className="p-2 text-[11px]" data-testid={`stat-card-${r._id}`}>
                        <div className="flex justify-between items-center gap-2 mb-1">
                          <span className="font-mono text-[10px] text-gray-500">
                            {fmtDate(r.created_at)}
                          </span>
                          <StatusBadge status={r.status} />
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px]">
                          <span>
                            <span className="text-gray-500">Czas:</span>{" "}
                            <span className="font-mono">{fmtMs(r.latency_ms)}</span>
                          </span>
                          <span>
                            <span className="text-gray-500">Tokeny:</span>{" "}
                            <span className="font-mono">{(r.tokens?.total || 0).toLocaleString("pl-PL")}</span>
                          </span>
                          <span>
                            <span className="text-gray-500">Koszt:</span>{" "}
                            <span className="font-mono">{fmtCost(r.estimated_cost_usd)}</span>
                          </span>
                        </div>
                        <div className="text-[10px] text-gray-600 mt-1">
                          {r.provider} · {r.model?.replace(/-2025\d+$/, "") || "—"}
                        </div>
                        {r.status === "error" && (
                          <div className="text-[10px] text-red-700 mt-0.5">
                            {r.error_type && <span className="font-mono">[{r.error_type}] </span>}
                            {r.error_message}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};

// Helper components (skoncentrowane w tym pliku żeby nie rozdmuchiwać drzewa).
const StatCard = ({ label, value, className = "", testId }) => (
  <div
    className="border border-gray-200 bg-white rounded p-2"
    data-testid={testId ? `dev-payment-agent-${testId}` : undefined}
  >
    <div className="text-[9px] uppercase tracking-wide text-gray-500 mb-0.5">{label}</div>
    <div className={`text-sm font-semibold font-mono text-gray-900 ${className}`}>{value}</div>
  </div>
);

const StatusBadge = ({ status }) => {
  if (status === "success") {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium border bg-emerald-50 text-emerald-700 border-emerald-200">
        <CheckCircle2 className="w-2.5 h-2.5" /> OK
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium border bg-red-50 text-red-700 border-red-200">
      <AlertTriangle className="w-2.5 h-2.5" /> ERR
    </span>
  );
};



/** MBankPlaygroundSection — DIAGNOSTYKA integracji mBank PSD2 XS2A.
 *  Na tym etapie: architektura + placeholdery („W przygotowaniu”). Nie
 *  implementujemy całej integracji — tylko przygotowujemy miejsce pod przyszłe
 *  narzędzia developerskie zgodnie ze specem P0 mBank PSD2 XS2A v2. */
const MBankPlaygroundSection = () => {
  return (
    <div className="space-y-5" data-testid="dev-section-mbank">
      <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          Centrum diagnostyczne integracji mBank PSD2 XS2A. Endpointy backendu są w trakcie
          implementacji (P0 wg PRD2). Poniżej architektura sekcji + placeholdery pod przyszłe
          narzędzia developerskie. Nie klikaj w placeholderach — nie robią nic funkcjonalnego.
        </span>
      </div>

      <MBankPanel
        testId="mbank-panel-status"
        icon={CheckCircle2}
        title="Status połączenia"
        items={[
          { label: "OAuth",          value: "—", hint: "Etap autoryzacji: Authorization Code + PKCE" },
          { label: "Consent",        value: "—", hint: "Aktualny consent ID + jego ważność" },
          { label: "AIS",            value: "—", hint: "Account Information Service — status subskrypcji" },
          { label: "Środowisko",     value: "sandbox", hint: "Sandbox / Production (z .env: MBANK_ENV)" },
          { label: "Status ogólny",  value: "not_configured" },
        ]}
      />

      <MBankPanel
        testId="mbank-panel-tokens"
        icon={Sparkles}
        title="Tokeny"
        items={[
          { label: "Access Token",   value: "—", hint: "OAuth2 access token (skrót SHA-8 wystarczy w UI)" },
          { label: "Refresh Token",  value: "—", hint: "Bezpiecznie w backendzie — tu tylko status obecności" },
          { label: "expires_at",     value: "—", hint: "ISO datetime UTC" },
          { label: "TTL pozostały",  value: "—", hint: "MM:SS do wygaśnięcia" },
        ]}
        actions={[
          { label: "Ręczny refresh", testId: "mbank-refresh-btn" },
        ]}
      />

      <MBankPanel
        testId="mbank-panel-accounts"
        icon={Wallet}
        title="Rachunki"
        items={[
          { label: "Liczba rachunków", value: "—" },
          { label: "IBAN główny",      value: "—" },
          { label: "Saldo",            value: "—" },
          { label: "Waluta",           value: "—" },
        ]}
        actions={[
          { label: "Pobierz listę rachunków", testId: "mbank-list-accounts-btn" },
        ]}
      />

      <MBankPanel
        testId="mbank-panel-history"
        icon={Database}
        title="Historia transakcji"
        items={[
          { label: "Zakres dat",     value: "—" },
          { label: "Liczba rekordów", value: "—" },
          { label: "Ostatnia synchronizacja", value: "—" },
        ]}
        actions={[
          { label: "Pobierz transakcje (7 dni)", testId: "mbank-fetch-7d-btn" },
          { label: "Pobierz transakcje (90 dni)", testId: "mbank-fetch-90d-btn" },
        ]}
      />

      <MBankPanel
        testId="mbank-panel-raw"
        icon={FileCode2}
        title="RAW response"
        items={[
          { label: "Ostatni endpoint",   value: "—" },
          { label: "Rozmiar odpowiedzi", value: "—" },
        ]}
        actions={[
          { label: "Pokaż pełny JSON",   testId: "mbank-show-raw-btn" },
          { label: "Kopiuj JSON",        testId: "mbank-copy-raw-btn" },
        ]}
      />

      <MBankPanel
        testId="mbank-panel-diag"
        icon={AlertTriangle}
        title="Diagnostyka"
        items={[
          { label: "Ostatni request",       value: "—", hint: "HTTP metoda + endpoint + timestamp" },
          { label: "Ostatni response",      value: "—", hint: "HTTP status + rozmiar body" },
          { label: "Czas odpowiedzi",       value: "—", hint: "milisekundy" },
          { label: "Kod HTTP",              value: "—" },
          { label: "Komunikat błędu",       value: "—", hint: "detail z response body" },
          { label: "Rate limit remaining",  value: "—", hint: "Nagłówek X-RateLimit-Remaining od mBank" },
        ]}
      />
    </div>
  );
};

/** MBankPanel — powtarzalna karta sekcji Playground mBank. Placeholder value = „—". */
const MBankPanel = ({ testId, icon: Icon, title, items = [], actions = [] }) => (
  <div data-testid={testId}>
    <div className="flex items-center gap-2 mb-2.5">
      <Icon className="w-4 h-4 text-amber-600" />
      <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">{title}</h2>
      <span className="ml-auto text-[10px] uppercase tracking-wide text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
        W przygotowaniu
      </span>
    </div>
    <Card className="border border-gray-200">
      <CardContent className="p-3 sm:p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {items.map((it) => (
            <div key={it.label} className="rounded bg-gray-50 border border-gray-100 px-2.5 py-1.5">
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-[10px] uppercase tracking-wide text-gray-500 truncate">{it.label}</div>
                <div className="font-mono text-xs font-semibold text-gray-400">{it.value}</div>
              </div>
              {it.hint && (
                <div className="text-[10px] text-gray-400 mt-0.5 leading-tight">{it.hint}</div>
              )}
            </div>
          ))}
        </div>
        {actions.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
            {actions.map((a) => (
              <Button
                key={a.label}
                type="button"
                variant="outline"
                size="sm"
                disabled
                data-testid={a.testId}
                title="Endpoint w przygotowaniu"
              >
                {a.label}
              </Button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  </div>
);

/** XmlKsefSection — narzędzia serwisowe KSeF: Backfill XML + skrót do kolejki dziennej. */
const XmlKsefSection = () => (
  <div className="space-y-5" data-testid="dev-section-xml">
    <div className="bg-indigo-50 border border-indigo-200 rounded p-3 text-xs text-indigo-900 flex items-start gap-2">
      <FileCode2 className="w-4 h-4 shrink-0 mt-0.5" />
      <span>
        Narzędzia serwisowe integracji KSeF: uzupełnianie brakujących danych z faktur XML,
        diagnostyka kolejki dziennej, rate-limity. Nic nie usuwa. Bezpieczne w PRODUCTION,
        ale wpływa na wykorzystanie limitu KSeF (~64 req/h).
      </span>
    </div>

    {/* Backfill XML — istniejący komponent, przeniesiony 1:1. */}
    <BackfillXmlCard />

    {/* Skróty do stron KSeF (kolejka, historia synchronizacji). */}
    <div>
      <div className="flex items-center gap-2 mb-2.5">
        <ExternalLink className="w-4 h-4 text-indigo-700" />
        <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
          Skróty do modułu KSeF
        </h2>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <KsefShortcut
          to="/settings"
          label="Konfiguracja KSeF"
          hint="NIP firmy, certyfikat mTLS, środowisko (test/prod)"
          testId="dev-xml-shortcut-settings"
        />
        <KsefShortcut
          to="/faktury"
          label="Kolejka dzienna + historia synchronizacji"
          hint="Rate-limit 64 req/h, pauzy między dniami, retry"
          testId="dev-xml-shortcut-queue"
        />
      </div>
    </div>
  </div>
);

const KsefShortcut = ({ to, label, hint, testId }) => (
  <Link
    to={to}
    className="rounded border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/40 px-3 py-2 flex items-start gap-2 transition-colors"
    data-testid={testId}
  >
    <ExternalLink className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
    <div className="min-w-0">
      <div className="text-sm font-semibold text-gray-900">{label}</div>
      <div className="text-[11px] text-gray-500 mt-0.5 leading-tight">{hint}</div>
    </div>
  </Link>
);

/** StatTile — pojedynczy licznik. */
const StatTile = ({ label, value, loading, testId }) => (
  <div className="rounded bg-gray-50 border border-gray-100 px-2.5 py-2" data-testid={testId}>
    <div className="text-[10px] text-gray-500 truncate">{label}</div>
    <div className="font-mono text-lg font-semibold text-gray-900">
      {loading ? "—" : (value ?? 0)}
    </div>
  </div>
);

/** InfoRow — wiersz label + value w gridzie. */
const InfoRow = ({ label, value }) => (
  <div className="rounded bg-gray-50 border border-gray-100 px-2.5 py-1.5">
    <div className="text-[10px] text-gray-500 truncate">{label}</div>
    <div className="font-mono text-xs font-semibold text-gray-800 break-words">{String(value)}</div>
  </div>
);

/** PlaceholderCard — jednolity szablon „W przygotowaniu”. */
const PlaceholderCard = ({ testId, children }) => (
  <Card className="border border-amber-200 bg-amber-50/40" data-testid={testId}>
    <CardContent className="p-3 sm:p-4 text-xs text-amber-900 flex items-start gap-2">
      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
      <div>{children}</div>
    </CardContent>
  </Card>
);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" });
  } catch { return String(iso); }
};

/**
 * BackfillXmlCard — uzupełnia brakujące dane XML (bank_account / payment_method
 * / due_date) dla faktur już zaimportowanych do `invoices`. Bezpieczny,
 * nie usuwa, nie nadpisuje niepustych wartości. Endpoint:
 *   POST /api/ksef/inbox/backfill-imported-xml-data
 *
 * Może działać kilka minut (KSeF rate-limit 64/h). Gdy proxy ucina połączenie
 * po ~60s, endpoint dalej pracuje w tle — wystarczy uruchomić ponownie aby
 * zobaczyć narastający progres.
 */
const BackfillXmlCard = () => {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleRun = async () => {
    setRunning(true);
    setError("");
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/ksef/inbox/backfill-imported-xml-data`,
        {},
        { withCredentials: true, timeout: 600_000 },
      );
      setResult(res.data || {});
      toast.success("Uzupełnianie zakończone.");
    } catch (e) {
      // Gateway timeout 60s nie oznacza błędu — backend pracuje dalej.
      const msg = e?.response?.data?.detail || e?.message || "Błąd";
      setError(String(msg).slice(0, 300));
      if (e?.code === "ECONNABORTED" || msg.toLowerCase().includes("timeout")) {
        toast.message("Połączenie zostało zamknięte przez proxy, ale backfill nadal trwa w tle. Spróbuj ponownie za chwilę.");
      } else {
        toast.error("Backfill nie powiódł się — sprawdź logi backendu.");
      }
    } finally {
      setRunning(false);
    }
  };

  return (
    <div data-testid="dev-backfill-section">
      <div className="flex items-center gap-2 mb-2.5">
        <RefreshCw className="w-4 h-4 text-indigo-700" />
        <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
          Naprawa danych (KSeF XML)
        </h2>
      </div>
      <Card className="border border-indigo-200" data-testid="dev-backfill-card">
        <CardContent className="p-3 sm:p-4 space-y-3">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-md shrink-0 bg-indigo-50">
              <FileText className="w-4 h-4 text-indigo-700" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-gray-900">
                Uzupełnij dane XML dla zaimportowanych faktur
              </div>
              <div className="text-[11px] text-gray-600 break-words mt-0.5">
                Dla faktur w <code className="font-mono text-[10px]">invoices</code> z pustym{" "}
                <code className="font-mono text-[10px]">bank_account</code> pobiera pełen XML z KSeF
                (jeśli jeszcze nie pobrano) i synchronizuje{" "}
                <code className="font-mono text-[10px]">bank_account</code> /{" "}
                <code className="font-mono text-[10px]">payment_method</code> /{" "}
                <code className="font-mono text-[10px]">due_date</code>. Sekwencyjne (KSeF
                rate-limit ~64 req/h).
              </div>
              <div className="text-[11px] text-emerald-700 break-words mt-0.5">
                Nie usuwa danych. Nie nadpisuje niepustych wartości. Nie zmienia statusów faktur.
              </div>
            </div>
          </div>

          <Button
            onClick={handleRun}
            disabled={running}
            data-testid="dev-backfill-run-btn"
            className="bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-gray-300 w-full sm:w-auto"
          >
            {running
              ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              : <RefreshCw className="w-4 h-4 mr-1.5" />}
            {running ? "Trwa uzupełnianie…" : "Uzupełnij dane XML"}
          </Button>

          {result && (
            <div
              className="rounded border border-emerald-200 bg-emerald-50 p-3 space-y-2"
              data-testid="dev-backfill-result"
            >
              <div className="flex items-center gap-1.5 text-emerald-900">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-sm font-semibold">Zakończono</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px]">
                <ResultStat label="processed" value={result.processed} />
                <ResultStat label="updated" value={result.updated} tone="ok" />
                <ResultStat label="xml_fetched" value={result.xml_fetched} tone="ok" />
                <ResultStat label="already_had_xml" value={result.already_had_xml} />
                <ResultStat label="skipped" value={result.skipped} />
                <ResultStat label="failed" value={result.failed} tone={result.failed > 0 ? "warn" : "ok"} />
              </div>
              {(result.errors || []).length > 0 && (
                <div className="rounded border border-amber-200 bg-amber-50 p-2 text-[11px] max-h-48 overflow-auto">
                  <div className="font-medium text-amber-900 mb-1">
                    Błędy ({result.errors.length}):
                  </div>
                  <ul className="space-y-0.5">
                    {result.errors.slice(0, 30).map((e, i) => (
                      <li key={i} className="font-mono break-all text-amber-900">
                        <span className="text-amber-700">{e.ksef_number}</span>: {e.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {error && !result && (
            <div
              className="rounded border border-rose-200 bg-rose-50 p-3 text-[11px] text-rose-900"
              data-testid="dev-backfill-error"
            >
              {error}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const ResultStat = ({ label, value, tone }) => {
  const cls =
    tone === "ok" ? "text-emerald-800"
    : tone === "warn" ? "text-amber-800"
    : "text-gray-800";
  return (
    <div className="rounded bg-white border border-gray-200 px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`font-mono text-base font-semibold ${cls}`}>{value ?? "—"}</div>
    </div>
  );
};
